import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, ClipboardList, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';

interface CreateAssetIssueFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  employeeId: string;
  purpose: string;
  notes: string;
}

interface FormErrors {
  employeeId?: string;
  purpose?: string;
}

export default function CreateAssetIssueFormModal({ open, onClose, onSuccess }: CreateAssetIssueFormModalProps) {
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState<FormData>({
    employeeId: '',
    purpose: '',
    notes: '',
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch employees
  const { data: employeesData } = useQuery({
    queryKey: ['employeesForIssue'],
    queryFn: async () => {
      const response = await api.get('/employees?limit=200&status=ACTIVE');
      return response.data.data || [];
    },
    enabled: open,
  });

  const employees = employeesData || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const payload = {
        employeeId: data.employeeId,
        purpose: data.purpose,
        notes: data.notes || null,
      };
      const response = await api.post('/asset-issue-forms', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-forms'] });
      resetForm();
      onSuccess();
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to create form');
    },
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.employeeId) {
      newErrors.employeeId = 'Please select an employee';
    }

    if (!formData.purpose.trim()) {
      newErrors.purpose = 'Purpose is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    createMutation.mutate(formData);
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      purpose: '',
      notes: '',
    });
    setErrors({});
    setSubmitError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto m-4">
        <Card className="border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Create Asset Issue Form
              </CardTitle>
              <CardDescription>
                Create a new asset issuance form for an employee
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Error Alert */}
            {submitError && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/20">
                <AlertCircle className="w-5 h-5 text-destructive" />
                <p className="text-sm text-destructive">{submitError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Employee Selection */}
              <div className="space-y-2">
                <Label htmlFor="employeeId">
                  Employee <span className="text-destructive">*</span>
                </Label>
                <select
                  id="employeeId"
                  className={cn(
                    "w-full h-10 px-3 rounded-md border bg-background text-sm",
                    errors.employeeId ? "border-destructive" : "border-input"
                  )}
                  value={formData.employeeId}
                  onChange={(e) => {
                    setFormData({ ...formData, employeeId: e.target.value });
                    if (errors.employeeId) {
                      setErrors(prev => ({ ...prev, employeeId: undefined }));
                    }
                  }}
                >
                  <option value="">Select an employee</option>
                  {employees.map((emp: any) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName} ({emp.employeeId})
                      {emp.department?.name && ` - ${emp.department.name}`}
                    </option>
                  ))}
                </select>
                {errors.employeeId && (
                  <p className="text-sm text-destructive">{errors.employeeId}</p>
                )}
              </div>

              {/* Purpose */}
              <div className="space-y-2">
                <Label htmlFor="purpose">
                  Purpose <span className="text-destructive">*</span>
                </Label>
                <textarea
                  id="purpose"
                  className={cn(
                    "w-full min-h-[80px] p-3 rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring",
                    errors.purpose ? "border-destructive focus:ring-destructive" : "border-input"
                  )}
                  placeholder="e.g., New employee onboarding, Equipment upgrade..."
                  value={formData.purpose}
                  onChange={(e) => {
                    setFormData({ ...formData, purpose: e.target.value });
                    if (errors.purpose) {
                      setErrors(prev => ({ ...prev, purpose: undefined }));
                    }
                  }}
                />
                {errors.purpose && (
                  <p className="text-sm text-destructive">{errors.purpose}</p>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <textarea
                  id="notes"
                  className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Any additional notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create Form'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
